import { LOB } from '../types/master-data.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Edit, Trash2, ChevronRight, ChevronDown, Plus, Check, X, Search } from 'lucide-react';
import { LOBLevel } from '@/types';
import { Fragment, useState } from 'react';

interface InlineCreateData {
  name: string;
  code: string;
  level: number;
  parentId: string;
  pctGarantia: number | null;
  pctNacionalizacion: number | null;
}

interface LOBTableProps {
  lobs: LOB[];
  onEdit: (lob: LOB) => void;
  onDelete: (lob: LOB) => void;
  /** Fallback (abre el modal). Si onCreateInline esta presente, el "+" hace creacion inline. */
  onAddChild?: (parent: LOB) => void;
  /** Si esta definido, el "+" agrega una fila editable inline en vez de abrir el modal. */
  onCreateInline?: (data: InlineCreateData) => Promise<void>;
  isLoading?: boolean;
}

export function LOBTable({
  lobs,
  onEdit,
  onDelete,
  onAddChild,
  onCreateInline,
  isLoading,
}: LOBTableProps) {
  // Solo mostramos el skeleton si es la carga inicial (sin datos todavia).
  // En mutaciones (delete/create/update) isLoading=true pero ya hay data en
  // memoria — no queremos hacer flicker de pagina.
  const isInitialLoad = isLoading && (!lobs || lobs.length === 0);
  // IDs colapsados (cualquier nivel). Por defecto todo expandido.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const toggleCollapse = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Estado de creacion inline: { parentId, level, name, code, %garantia, %nac }
  const [pendingNew, setPendingNew] = useState<InlineCreateData | null>(null);
  const [savingNew, setSavingNew] = useState(false);

  const slugifyCode = (s: string, prefix: string) =>
    `${prefix}_${s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60)}`;

  const beginInlineCreate = (parent: LOB) => {
    // Normalizar level: la API devuelve string "SECTOR"/"VERTICAL"/"PRODUCT",
    // pero la DB usa numeros 1/2/3. Aceptamos ambos.
    const levelMap: Record<string, number> = { SECTOR: 1, VERTICAL: 2, PRODUCT: 3 };
    const rawLevel: any = parent.level;
    const parentLevel =
      typeof rawLevel === 'number'
        ? rawLevel
        : (levelMap[String(rawLevel)] ?? Number(rawLevel) ?? 1);
    const childLevel = parentLevel + 1;
    setPendingNew({
      name: '',
      code: '',
      level: childLevel,
      parentId: parent.id,
      pctGarantia: null,
      pctNacionalizacion: null,
    });
    // Asegurarse de que el padre este expandido
    setCollapsed((prev) => {
      if (!prev.has(parent.id)) return prev;
      const next = new Set(prev);
      next.delete(parent.id);
      return next;
    });
  };

  const cancelInline = () => {
    setPendingNew(null);
    setSavingNew(false);
  };

  const saveInline = async () => {
    if (!pendingNew || !onCreateInline) return;
    if (!pendingNew.name.trim()) return;
    const parent = lobs.find((l) => l.id === pendingNew.parentId);
    const prefix = parent?.code || 'NEW';
    const finalData: InlineCreateData = {
      ...pendingNew,
      name: pendingNew.name.trim(),
      code: (pendingNew.code.trim() || slugifyCode(pendingNew.name, prefix)).toUpperCase(),
    };
    setSavingNew(true);
    try {
      await onCreateInline(finalData);
      setPendingNew(null);
    } finally {
      setSavingNew(false);
    }
  };

  if (isInitialLoad) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Cargando LOBs...</p>
      </div>
    );
  }

  if (!lobs || lobs.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground font-medium">No hay LOBs configurados</p>
        <p className="text-xs text-muted-foreground mt-1">
          Crea líneas de negocio en 3 niveles: Sector → Vertical → Marca para organizar tus
          cotizaciones
        </p>
      </div>
    );
  }

  // Build hierarchy
  // level puede ser número (1,2,3) desde la DB o string enum ('SECTOR','VERTICAL','PRODUCT')
  const isLevel = (l: LOB, level: LOBLevel | number) => {
    if (typeof l.level === 'number') {
      return level === LOBLevel.SECTOR
        ? l.level === 1
        : level === LOBLevel.VERTICAL
          ? l.level === 2
          : l.level === 3;
    }
    return l.level === level;
  };

  // Búsqueda: un LOB entra si su nombre (o código) matchea, o si algún
  // descendiente matchea — así el árbol conserva el camino hasta la coincidencia.
  const normalizado = (t: string) =>
    t
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const q = normalizado(query.trim());
  const visibles = ((): Set<string> | null => {
    if (!q) return null;
    const byId = new Map((lobs ?? []).map((l) => [l.id, l]));
    const set = new Set<string>();
    for (const l of lobs ?? []) {
      if (normalizado(`${l.name} ${l.code ?? ''}`).includes(q)) {
        let cur: LOB | undefined = l;
        while (cur && !set.has(cur.id)) {
          set.add(cur.id);
          cur = cur.parentId ? byId.get(cur.parentId) : undefined;
        }
      }
    }
    return set;
  })();
  const buscando = visibles !== null;
  const pasaFiltro = (l: LOB) => !visibles || visibles.has(l.id);

  const buildHierarchy = () => {
    const sectors = lobs?.filter((l) => isLevel(l, LOBLevel.SECTOR) && pasaFiltro(l)) ?? [];
    const verticals = lobs?.filter((l) => isLevel(l, LOBLevel.VERTICAL) && pasaFiltro(l)) ?? [];
    const products = lobs?.filter((l) => isLevel(l, LOBLevel.PRODUCT) && pasaFiltro(l)) ?? [];

    const result: any[] = [];
    // Todo lo que tiene un lugar en el árbol, esté visible o colapsado.
    // Sin esto, los hijos de un sector colapsado caían al bloque de huérfanos
    // y aparecían como filas sueltas de nivel 0.
    const placed = new Set<string>();

    sectors.forEach((sector) => {
      placed.add(sector.id);
      const sectorVerticals = verticals.filter((v) => v.parentId === sector.id);
      const sectorProducts = products.filter((p) =>
        sectorVerticals.some((sv) => sv.id === p.parentId)
      );
      sectorVerticals.forEach((v) => placed.add(v.id));
      sectorProducts.forEach((p) => placed.add(p.id));
      result.push({
        ...sector,
        depth: 0,
        hasChildren: sectorVerticals.length > 0,
        childCount: sectorVerticals.length,
        descendantCount: sectorVerticals.length + sectorProducts.length,
      });

      if (!buscando && collapsed.has(sector.id)) return; // ocultar verticales y productos

      sectorVerticals.forEach((vertical) => {
        const verticalProducts = products.filter((p) => p.parentId === vertical.id);
        result.push({
          ...vertical,
          depth: 1,
          hasChildren: verticalProducts.length > 0,
          childCount: verticalProducts.length,
          descendantCount: verticalProducts.length,
        });

        if (!buscando && collapsed.has(vertical.id)) return; // ocultar productos

        verticalProducts.forEach((product) => {
          result.push({
            ...product,
            depth: 2,
            hasChildren: false,
            childCount: 0,
            descendantCount: 0,
          });
        });
      });
    });

    // Agregar items sin padre válido que no entraron en la jerarquía
    lobs
      .filter((l) => !placed.has(l.id) && pasaFiltro(l))
      .forEach((orphan) => {
        result.push({ ...orphan, depth: 0, hasChildren: false });
      });

    return result;
  };

  const hierarchy = buildHierarchy();

  // Un nivel no es un estado: sin rojo ni verde (el rojo de «Sector» se leía como error).
  const getLevelColor = (level: LOBLevel | number) => {
    if (level === LOBLevel.SECTOR || level === 1) return 'info';
    if (level === LOBLevel.VERTICAL || level === 2) return 'secondary';
    return 'outline';
  };

  const getLevelLabel = (level: LOBLevel | number): string => {
    if (level === 1 || level === LOBLevel.SECTOR) return 'Sector';
    if (level === 2 || level === LOBLevel.VERTICAL) return 'Vertical';
    // El tercer nivel es la marca (los botones dicen «+ Marca»).
    if (level === 3 || level === LOBLevel.PRODUCT) return 'Marca';
    return String(level);
  };

  return (
    <div className="space-y-3">
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar LOB, CeBe o marca..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-8"
          aria-label="Buscar LOB"
        />
      </div>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>SAP</TableHead>
              <TableHead>Nivel</TableHead>
              <TableHead>Parámetros</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hierarchy.map((lob) => (
              <Fragment key={lob.id}>
                <TableRow>
                  <TableCell>
                    <div
                      className="flex items-center"
                      style={{ paddingLeft: `${lob.depth * 24}px` }}
                    >
                      {lob.hasChildren ? (
                        <button
                          type="button"
                          onClick={() => toggleCollapse(lob.id)}
                          className="mr-1 text-muted-foreground hover:text-foreground rounded p-0.5 -ml-0.5"
                          aria-label={collapsed.has(lob.id) ? 'Expandir' : 'Colapsar'}
                        >
                          {collapsed.has(lob.id) ? (
                            <ChevronRight className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      ) : (
                        // Espaciado para alinear filas hoja con las que tienen chevron
                        <span className="w-5 inline-block" />
                      )}
                      <span className={lob.depth === 0 ? 'font-semibold' : ''}>{lob.name}</span>
                      {lob.hasChildren && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({lob.descendantCount})
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {(lob as any).sapCode || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getLevelColor(lob.level)}>{getLevelLabel(lob.level)}</Badge>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const hasMargin =
                        lob.targetMarginPercentage != null || lob.floorMarginPercentage != null;
                      const hasPricer =
                        (lob as any).pctGarantia != null || (lob as any).pctNacionalizacion != null;
                      if (!hasMargin && !hasPricer) {
                        return <span className="text-xs text-muted-foreground">Usa modelo</span>;
                      }
                      return (
                        <div className="flex flex-col gap-0.5 text-xs font-mono">
                          {hasMargin && (
                            <span>
                              {lob.floorMarginPercentage != null && (
                                <span>Piso: {lob.floorMarginPercentage}%</span>
                              )}
                              {lob.floorMarginPercentage != null &&
                                lob.targetMarginPercentage != null && <span> · </span>}
                              {lob.targetMarginPercentage != null && (
                                <span>Obj: {lob.targetMarginPercentage}%</span>
                              )}
                            </span>
                          )}
                          {hasPricer && (
                            <span className="flex gap-1 flex-wrap">
                              {(lob as any).pctGarantia != null && (
                                <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700">
                                  Gar: {(lob as any).pctGarantia}%
                                </span>
                              )}
                              {(lob as any).pctNacionalizacion != null && (
                                <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-700">
                                  Nac: {(lob as any).pctNacionalizacion}%
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={lob.isActive ? 'success' : 'default'}>
                      {lob.isActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {(onAddChild || onCreateInline) &&
                        !isLevel(lob, LOBLevel.PRODUCT) &&
                        lob.level !== 3 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-primary"
                                onClick={() => {
                                  if (onCreateInline) beginInlineCreate(lob);
                                  else if (onAddChild) onAddChild(lob);
                                }}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                {isLevel(lob, LOBLevel.SECTOR) || lob.level === 1
                                  ? 'Vertical'
                                  : 'Marca'}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {isLevel(lob, LOBLevel.SECTOR) || lob.level === 1
                                ? `Agregar una Vertical dentro de ${lob.name}`
                                : `Agregar una Marca dentro de ${lob.name}`}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/*
                          Sin `aria-label` estos botones eran solo un ícono: un
                          lector de pantalla los anunciaba como "botón" a secas, y
                          desde una prueba había que adivinarlos por posición — lo
                          que fallaba, porque el PRIMER botón de la fila es el de
                          desplegar los LOB hijos, no el de editar.
                        */}
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Editar ${lob.name}`}
                            onClick={() => onEdit(lob)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Editar este LOB</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/*
                          Sin `aria-label` estos botones eran solo un ícono: un
                          lector de pantalla los anunciaba como "botón" a secas, y
                          desde una prueba había que adivinarlos por posición — lo
                          que fallaba, porque el PRIMER botón de la fila es el de
                          desplegar los LOB hijos, no el de editar.
                        */}
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Eliminar ${lob.name}`}
                            onClick={() => onDelete(lob)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Eliminar este LOB</TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
                {pendingNew && pendingNew.parentId === lob.id && (
                  <TableRow className="bg-primary/5">
                    <TableCell>
                      <div
                        className="flex items-center gap-2"
                        style={{ paddingLeft: `${(lob.depth + 1) * 24}px` }}
                      >
                        <span className="w-5 inline-block" />
                        <Input
                          autoFocus
                          placeholder={
                            pendingNew.level === 2 ? 'Nombre de la vertical' : 'Nombre de la marca'
                          }
                          value={pendingNew.name}
                          onChange={(e) =>
                            setPendingNew((p) => (p ? { ...p, name: e.target.value } : p))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void saveInline();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelInline();
                            }
                          }}
                          className="h-8 w-56"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={pendingNew.level === 2 ? 'secondary' : 'outline'}>
                        {pendingNew.level === 2 ? 'Vertical' : 'Marca'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {pendingNew.level === 3 ? (
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="% Gar."
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={pendingNew.pctGarantia ?? ''}
                            onChange={(e) =>
                              setPendingNew((p) =>
                                p
                                  ? {
                                      ...p,
                                      pctGarantia:
                                        e.target.value === '' ? null : Number(e.target.value),
                                    }
                                  : p
                              )
                            }
                            className="h-8 w-20 text-xs"
                          />
                          <Input
                            placeholder="% Nac."
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={pendingNew.pctNacionalizacion ?? ''}
                            onChange={(e) =>
                              setPendingNew((p) =>
                                p
                                  ? {
                                      ...p,
                                      pctNacionalizacion:
                                        e.target.value === '' ? null : Number(e.target.value),
                                    }
                                  : p
                              )
                            }
                            className="h-8 w-20 text-xs"
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="default">Nuevo</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-600"
                              onClick={() => void saveInline()}
                              disabled={savingNew || !pendingNew.name.trim()}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Guardar (Enter)</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={cancelInline}
                              disabled={savingNew}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Cancelar (Esc)</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
        {buscando && hierarchy.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground">Sin resultados para «{query}».</p>
        )}
      </div>
    </div>
  );
}

export default LOBTable;
